import type { ClientId, ContractId } from "./content";
import type { AchievementId, ContractType, Grade, SkillId, ToolId } from "./enums";

export interface ResultHistoryEntry {
  contractId: ContractId;
  grade: Grade;
  score: number;
  money: number;
  xp: number;
  at: number;
  taskScores: number[];
  /** Task types as they were when the contract ran; absent in pre-2026-09 saves. */
  taskTypes?: ContractType[];
}

export interface UrgentOffer {
  contractId: ContractId;
  deadlineMs: number;
  bonusPct: number;
  durationMs: number;
}

export interface OfferBoardState {
  available: ContractId[];
  urgent: UrgentOffer | null;
  /**
   * Real-time timestamp before which no new urgent offer may spawn. Set when an
   * urgent offer ends (consumed or expired); null/undefined means no cooldown is
   * pending and the slot may be filled immediately.
   */
  nextUrgentAtMs?: number | null;
  /** Contract id of the previous urgent offer, so the next one avoids repeating it. */
  lastUrgentId?: ContractId | null;
}

export interface Player {
  id: string;
  name: string;
  level: number;
  xp: number;
  money: number;
  reputation: number;
  skillPoints: number;
  skills: Record<SkillId, number>;
  ownedTools: ToolId[];
  completedContracts: ContractId[];
  achievements: AchievementId[];
}

export interface Agency {
  name: string;
}

export interface StreakState {
  current: number;
  longest: number;
  lastClaimDate: string;
}

export interface TrainingState {
  bought: number;
}

export const SAVE_VERSION = 8 as const;

/**
 * Persisted progress. Static client data lives in the content layer (SSOT);
 * only the mutable per-client trust is saved here, keyed by client id.
 * completedContracts / ownedTools / achievements live on Player (single home).
 */
export interface SaveGame {
  saveVersion: number;
  player: Player;
  agency: Agency;
  clientTrust: Record<ClientId, number>;
  unlockedClients: ClientId[];
  streak: StreakState;
  offers: OfferBoardState;
  lastSaveTime: number;
  resultHistory: ResultHistoryEntry[];
  training: TrainingState;
}
